import { selectCurrency } from '@/redux/config/configSlice';
import { ItemType, Unit } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { Button, Card, Modal, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Text, Title } = Typography;

interface UnitCalculatorProps {
  item: ItemType;
  onClose: () => void;
  onSubmit: (item: ItemType, selectedUnit?: Unit) => void;
}

type SelectionOption = 'default' | Unit;

const UnitCalculator = ({ item, onClose, onSubmit }: UnitCalculatorProps) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<SelectionOption>('default');
  const currency = useAppSelector(selectCurrency);
  const availableUnits = item.units?.filter((unit) => unit.enabled) || [];

  // Determine the display price based on priority:
  // 1. customerTierPriceAfterTax (if exists and customer is selected)
  // 2. priceAfterTax (if exists)
  // 3. price (fallback)
  const displayPrice = item.customerTierPriceAfterTax ?? item.priceAfterTax ?? item.price;

  const handleSubmit = () => {
    if (selectedOption === 'default') {
      onSubmit(item); // Submit without any unit (default behavior)
    } else {
      onSubmit(item, selectedOption);
    }
  };

  const isUnitSelected = (unit: Unit): boolean => {
    return selectedOption !== 'default' && (selectedOption as Unit).name === unit.name;
  };

  if (availableUnits.length === 0) {
    onSubmit(item);
    return null;
  }

  return (
    <Modal
      open
      onCancel={onClose}
      title={t('select_selling_option')}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common:cancel')}
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} disabled={!selectedOption}>
          {t('common:confirm')}
        </Button>,
      ]}
    >
      <div className="space-y-4">
        <Title level={5} className="mb-2">
          {t('select_selling_option')}
        </Title>

        {/* Default Option */}
        <Card
          hoverable
          onClick={() => setSelectedOption('default')}
          className={`mb-2 ${selectedOption === 'default' ? 'border-blue-500' : ''}`}
        >
          <div className="flex justify-between items-center">
            <div>
              <Text strong>{t('sell_as_is')}</Text>
              <div className="text-sm text-gray-500">{t('use_default_unit')}</div>
            </div>
            <Text>
              {displayPrice} {currency} / {t('common:piece')}
            </Text>
          </div>
        </Card>

        {availableUnits.length > 0 && (
          <>
            <div className="border-t border-gray-200 my-4">
              <Text type="secondary" className="text-sm">
                {t('or_select_custom_unit')}
              </Text>
            </div>

            {/* Custom Units */}
            <div className="space-y-2">
              {availableUnits.map((unit) => (
                <Card
                  key={unit.name}
                  hoverable
                  onClick={() => setSelectedOption(unit)}
                  className={isUnitSelected(unit) ? 'border-blue-500' : ''}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <Text strong>{unit.name}</Text>
                      {unit.conversionFactor && unit.conversionFactor !== 1 && (
                        <div className="text-sm text-gray-500">
                          {t('conversion_factor', { factor: unit.conversionFactor })}
                        </div>
                      )}
                    </div>
                    <Text>
                      {unit.price} {currency}
                    </Text>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default UnitCalculator;
